import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setDropdownOpen(false);
      }
      // Ignore clicks on the hamburger button itself, otherwise this
      // would close the menu right before the button's onClick reopens it.
      const clickedHamburger = hamburgerRef.current && hamburgerRef.current.contains(target);
      const clickedInsideMenu = mobileMenuRef.current && mobileMenuRef.current.contains(target);
      if (!clickedHamburger && !clickedInsideMenu) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleMobileToggle = () => {
    setMobileMenuOpen((prev) => !prev);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16 md:h-18">
        <Link
          to="/"
          className={`font-heading text-xl md:text-2xl font-semibold tracking-tight transition-colors duration-300 whitespace-nowrap ${
            scrolled ? 'text-foreground-900' : 'text-white'
          }`}
        >
          gtbuy<span className="font-light">.art</span>
        </Link>

        <div className="hidden md:flex items-center gap-6" ref={dropdownRef}>
          <div className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`flex items-center gap-1.5 text-sm font-medium transition-colors duration-300 cursor-pointer whitespace-nowrap ${
                scrolled ? 'text-foreground-700 hover:text-foreground-900' : 'text-white/90 hover:text-white'
              }`}
            >
              <span>Categories</span>
              <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-56 bg-background-50 rounded-lg border border-background-200/70 py-2 max-h-80 overflow-y-auto">
                {categories.map((cat) => (
                  <a
                    key={cat.seq}
                    href={cat.href}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors whitespace-nowrap"
                    onClick={() => setDropdownOpen(false)}
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/article-gtbuy-sheet"
            className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
              scrolled ? 'text-foreground-700 hover:text-foreground-900' : 'text-white/90 hover:text-white'
            }`}
          >
            Articles
          </Link>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="bg-primary-500 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            gtbuy sheet
            <i className="ri-arrow-right-up-line ml-1.5"></i>
          </a>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="border border-white text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer"
          >
            buy it now
          </a>
        </div>

        <button
          ref={hamburgerRef}
          aria-label="Toggle menu"
          className={`md:hidden flex items-center justify-center w-10 h-10 cursor-pointer ${
            scrolled ? 'text-foreground-900' : 'text-white'
          }`}
          onClick={handleMobileToggle}
        >
          <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      <div ref={mobileMenuRef}>
        {mobileMenuOpen && (
          <div
            className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-4 flex flex-col gap-2 max-h-[calc(100vh-4rem)] overflow-y-auto overscroll-contain"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-1">Categories</p>
            {categories.map((cat) => (
              <a
                key={cat.seq}
                href={cat.href}
                target="_blank"
                rel="noopener nofollow"
                className="block py-2 text-sm text-foreground-700 hover:text-foreground-900 transition-colors"
                onClick={closeMobileMenu}
              >
                {cat.name}
              </a>
            ))}
            <div className="border-t border-background-200/70 pt-3 mt-2">
              <Link
                to="/article-gtbuy-sheet"
                className="block py-2 text-sm text-foreground-700 hover:text-foreground-900 transition-colors"
                onClick={closeMobileMenu}
              >
                Articles
              </Link>
            </div>
            <div className="flex flex-col gap-2 mt-2 pb-6">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="bg-primary-500 text-white text-center px-4 py-2.5 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                gtbuy sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="border border-foreground-300 text-foreground-700 text-center px-4 py-2.5 rounded-full text-sm font-medium hover:bg-foreground-100 transition-colors whitespace-nowrap"
              >
                buy it now
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}